import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as React from 'react';
import { useRouter } from 'next/router';
import type { MotionValue } from 'motion/react';
import {
  DEFAULT_MEMBER_EXPERIENCE_CHAPTER,
  getMemberExperienceChapter,
  hasActiveSubscriptionFor,
} from 'lib/subscriptions';
import {
  getChapterStatus,
  getJourneyDestination,
  isPositionAhead,
  positionFromBookmark,
  DS01_PRODUCT_SLUG,
  PRODUCT_CODE_BY_SLUG,
  POST_LOGIN_ENTRY_PARAM,
  POST_LOGIN_ENTRY_VALUE,
  SUBSCRIPTION_BENEFITS_ROUTE,
} from 'lib/member-experience';
import type { BookmarkedPosition } from 'lib/member-experience';
import type { ProductCodes } from 'lib/utils/get-product-specific-code';
import type { FooterDisclaimerConfig } from 'components/home/FooterNavigation/footer-disclaimer';
import { useGetSubscriptions } from 'api/subscription';
import { useGetBookmark, useUpsertBookmark } from 'api/bookmark';
import { UserContext } from 'context-providers/user-provider';
import { trackEvent } from 'lib/analytics';
import { forceSessionReplay } from 'lib/datadog-rum';
import { useScrollHold } from 'hooks/use-scroll-hold';
import { useIsOneTimePurchaser } from 'hooks/use-is-one-time-purchaser';

type ModuleViewedMeta = {
  moduleTitle: string;
  sections: Record<number, { sectionKey: string; sectionTitle: string }>;
  // Completion emits this section, not the last real one.
  enderSection: number;
};

// Values come from the Module Viewed event spec sheet. module_key is one
// shared value across chapters there (its example uses "MRD").
const MODULE_VIEWED_KEY = 'MRD';

const MODULE_VIEWED_META: Record<number, ModuleViewedMeta> = {
  1: {
    moduleTitle: 'Chapter 1',
    sections: {
      1: { sectionKey: 'welcome-kit', sectionTitle: 'Welcome Kit' },
      2: { sectionKey: 'gut-science', sectionTitle: 'Gut Science' },
      3: { sectionKey: 'acclimation', sectionTitle: 'Acclimation' },
      4: { sectionKey: 'whats-next', sectionTitle: "What's Next" },
      5: { sectionKey: 'ender', sectionTitle: 'Ender' },
    },
    enderSection: 5,
  },
  2: {
    moduleTitle: 'Chapter 2',
    sections: {
      1: { sectionKey: 'the-science', sectionTitle: 'The Science' },
      2: { sectionKey: 'community', sectionTitle: 'Community' },
      3: { sectionKey: 'whats-next', sectionTitle: "What's Next" },
      4: { sectionKey: 'ender', sectionTitle: 'Ender' },
    },
    enderSection: 4,
  },
  3: {
    moduleTitle: 'Chapter 3',
    sections: {
      1: { sectionKey: 'the-science', sectionTitle: 'The Science' },
      2: { sectionKey: 'whats-next', sectionTitle: "What's Next" },
      3: { sectionKey: 'ender', sectionTitle: 'Ender' },
    },
    enderSection: 3,
  },
  4: {
    moduleTitle: 'Chapter 4',
    sections: {
      1: {
        sectionKey: 'impact-education',
        sectionTitle: 'Impact & Education',
      },
      2: { sectionKey: 'co-biotics', sectionTitle: 'Co-Biotics' },
      3: {
        sectionKey: 'our-recommendation',
        sectionTitle: 'Our Recommendation',
      },
      4: { sectionKey: 'ender', sectionTitle: 'Ender' },
    },
    enderSection: 4,
  },
};

export const SECTION_QUERY_PARAM = 'section';

// Accepts a sectionKey ('gut-science') or the number itself so deep-links reuse
// the same section source the analytics event keys off.
export const getSectionNumberByKey = (
  chapter: number,
  value: string,
): number | null => {
  const sections = MODULE_VIEWED_META[chapter]?.sections;
  if (!sections) return null;

  const asNumber = Number(value);
  if (Number.isInteger(asNumber) && sections[asNumber]) return asNumber;

  const match = Object.entries(sections).find(
    ([, meta]) => meta.sectionKey === value,
  );
  return match ? Number(match[0]) : null;
};

const viewedModules = new Set<string>();

// Deduped per session so a remounted provider can't re-report a position.
// Chapters without metadata still bookmark but don't emit.
const trackModuleViewed = (
  chapter: number,
  section: number,
  chapterCompleted: boolean,
) => {
  const module = MODULE_VIEWED_META[chapter];
  const sectionMeta = module?.sections[section];
  if (!module || !sectionMeta) return;

  const key = `${chapter}:${section}`;
  if (viewedModules.has(key)) return;
  viewedModules.add(key);

  trackEvent('Module Viewed', {
    module_key: MODULE_VIEWED_KEY,
    module_title: module.moduleTitle,
    module_index: chapter,
    section_key: sectionMeta.sectionKey,
    section_title: sectionMeta.sectionTitle,
    section_index: section,
    product: [DS01_PRODUCT_SLUG],
    chapter_completed: chapterCompleted,
    reduced_motion: window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches,
  });
};

// Scroll-through progress for the timeline bar/nav — unrelated to the TOC Card's
// own `progress` prop in the bottomsheet, which is not chapter completion.
// Don't wire the two together.
/** An entrance overlay's own exit, run before the wrapper fades. */
export type EntranceExit = () => Promise<unknown>;

interface IJourneyContext {
  activeChapterId: number | null;
  currentChapter: number;
  productCode: ProductCodes;
  isBottomsheetOpen: boolean;
  activeScrollMotionValue: MotionValue<number> | null;
  setActiveChapterId: (chapterId: number | null) => void;
  reportSectionReached: (section: number) => void;
  reportChapterCompleted: () => void;
  setIsBottomsheetOpen: (isOpen: boolean) => void;
  setActiveScrollMotionValue: (value: MotionValue<number> | null) => void;
  footerDisclaimerConfig?: FooterDisclaimerConfig;
  /** Landing calls this once its entrance has finished (or been skipped) so
   *  the TOC can auto-open on a member's first view of their current chapter. */
  markLandingSettled: () => void;
  /** Landing calls this on the entrance beat the scroll cue's label decodes on,
   *  ahead of settling, so the cue reads in as its arrow lands. */
  markScrollCuePlaced: () => void;
  /** True once the scroll cue's decode has been released (always true outside a
   *  provider). ChapterTimelineNav is a sibling of Landing, hence the hand-off. */
  isScrollCuePlaced: boolean;
  /** True once the member is cleared for the current path (experiment, chapter
   *  lock, bookmark). The entrance wrapper holds its exit until then. */
  isAllowed: boolean;
  /** True once the chapter's entrance wrapper has fully unmounted (always true on
   *  the hub, which has none). Gate anything that would move underneath it. */
  isEntranceComplete: boolean;
  /** Entrance overlays call this once they have played out. The optional exit
   *  runs before the wrapper fades, once the page is ready. */
  finishEntrance: (exit?: EntranceExit) => void;
  /** Set by finishEntrance for the current chapter; EntranceWrapper runs the exit. */
  entranceFinish: { exit?: EntranceExit } | null;
  /** EntranceWrapper calls this once its overlay is gone. */
  completeEntrance: () => void;
  /** Whether the layout should render the page. Also true during the auth wait,
   *  so SitePrivate can own its spinner and the /login redirect. */
  isGateOpen: boolean;
  /** Called by the deep-link once it has scrolled the section into place, so the
   *  entrance wrapper (which waits on it via isAllowed) can lift. */
  resolveDeepLink: () => void;
  registerSectionAnchor: (section: number, element: HTMLElement | null) => void;
  getSectionAnchor: (section: number) => HTMLElement | null;
}

const defaultContextValue: IJourneyContext = {
  activeChapterId: null,
  currentChapter: DEFAULT_MEMBER_EXPERIENCE_CHAPTER,
  productCode: PRODUCT_CODE_BY_SLUG[DS01_PRODUCT_SLUG],
  isBottomsheetOpen: false,
  activeScrollMotionValue: null,
  setActiveChapterId: () => {},
  reportSectionReached: () => {},
  reportChapterCompleted: () => {},
  setIsBottomsheetOpen: () => {},
  setActiveScrollMotionValue: () => {},
  footerDisclaimerConfig: undefined,
  markLandingSettled: () => {},
  markScrollCuePlaced: () => {},
  isScrollCuePlaced: true,
  isAllowed: true,
  isEntranceComplete: true,
  finishEntrance: () => {},
  entranceFinish: null,
  completeEntrance: () => {},
  isGateOpen: true,
  resolveDeepLink: () => {},
  registerSectionAnchor: () => {},
  getSectionAnchor: () => null,
};

const JourneyContext = createContext<IJourneyContext>(defaultContextValue);

export const useJourney = () => useContext(JourneyContext);

const JOURNEY_PRODUCT_CODE = PRODUCT_CODE_BY_SLUG[DS01_PRODUCT_SLUG];
const HOME_ROUTE = '/account/home';

// seed_core assigns the bucket; MSL only reads it off the user payload.
const MEMBER_REDESIGN_EXPERIMENT = 'member_redesign_july2026';
const MEMBER_REDESIGN_TEST_VARIATION = 'test';

// Single source for every redesign entry point: nav, chapter gate, post-login.
export const useIsJourneyMember = (): boolean => {
  const { checkUserExperiment } = useContext(UserContext);
  return checkUserExperiment(
    MEMBER_REDESIGN_EXPERIMENT,
    MEMBER_REDESIGN_TEST_VARIATION,
  );
};

// Reads the queries directly rather than JourneyContext: callers render
// outside JourneyProvider (global nav, /account/home).
const useJourneyRoute = (enabled: boolean) => {
  const { data: subscriptions, isLoading: isSubscriptionsLoading } =
    useGetSubscriptions();
  const { data: bookmark, isLoading: isBookmarkLoading } = useGetBookmark(
    JOURNEY_PRODUCT_CODE,
    enabled,
  );

  const currentChapter = getMemberExperienceChapter(
    subscriptions,
    JOURNEY_PRODUCT_CODE,
  );

  return {
    destination: getJourneyDestination(
      currentChapter,
      positionFromBookmark(bookmark),
    ),
    hasSubscription: hasActiveSubscriptionFor(
      subscriptions,
      JOURNEY_PRODUCT_CODE,
    ),
    isLoading: isSubscriptionsLoading || isBookmarkLoading,
  };
};

export const useJourneyDestination = (enabled: boolean): string =>
  useJourneyRoute(enabled).destination;

// On a fresh login (POST_LOGIN_HOME_ROUTE), sends a member with no
// subscriptions (a one-time purchaser) to the DS-01 subscribe page and a
// redesign-bucketed member with an active DS-01 subscription into Journey.
// Everyone else stays on home with the marker stripped; a plain visit to
// /account/home never redirects. Returns true while the page should keep
// showing its loading state.
export const usePostLoginJourneyRedirect = (): boolean => {
  const router = useRouter();
  const isJourneyMember = useIsJourneyMember();

  const isLoginEntry =
    router.isReady &&
    router.query[POST_LOGIN_ENTRY_PARAM] === POST_LOGIN_ENTRY_VALUE;

  const { isOneTimePurchaser, isResolving: isPurchaserResolving } =
    useIsOneTimePurchaser();
  const { destination, hasSubscription, isLoading } = useJourneyRoute(
    isLoginEntry && isJourneyMember,
  );

  const isResolving = isPurchaserResolving || (isJourneyMember && isLoading);
  const shouldStayHome =
    !isResolving &&
    !isOneTimePurchaser &&
    (!isJourneyMember || !hasSubscription);

  useEffect(() => {
    if (!isLoginEntry || isResolving) return;
    if (isOneTimePurchaser) {
      router.replace(SUBSCRIPTION_BENEFITS_ROUTE);
      return;
    }
    if (shouldStayHome) {
      router.replace(HOME_ROUTE, undefined, { shallow: true });
      return;
    }
    router.replace(destination);
  }, [
    router,
    isLoginEntry,
    isResolving,
    isOneTimePurchaser,
    shouldStayHome,
    destination,
  ]);

  return isLoginEntry && !shouldStayHome;
};

// main page does not need to pass chapterNumber. productCode is required —
// callers derive it from the route's product slug via PRODUCT_CODE_BY_SLUG,
// same source the dynamic chapter route already keys off of.
export const JourneyProvider: React.FC<{
  children: React.ReactNode;
  chapterNumber?: number;
  productCode: ProductCodes;
  footerDisclaimerConfig?: FooterDisclaimerConfig;
}> = ({ children, chapterNumber, productCode, footerDisclaimerConfig }) => {
  const router = useRouter();
  const { userState, isLoadingUser } = useContext(UserContext);
  const hasRedesignExperiment = useIsJourneyMember();

  const path = router.asPath.split(/[?#]/)[0];

  // Staging escape hatch so the team can preview chapters a member hasn't
  // unlocked yet. Never available in production.
  const hasChapterOverride =
    process.env.NEXT_PUBLIC_STAGING === 'true' &&
    router.query.override === 'true';

  // The member's furthest-reached chapter, derived once from their DS-01
  // subscriptions. Exposed on the context so the TOC can style each card, and
  // used here to gate the current chapter.
  const { data: subscriptions, isLoading: isChapterDataLoading } =
    useGetSubscriptions();
  const currentChapter = getMemberExperienceChapter(subscriptions, productCode);
  const status = chapterNumber
    ? getChapterStatus(chapterNumber, currentChapter)
    : undefined;

  const { data: bookmark, isLoading: isBookmarkLoading } =
    useGetBookmark(productCode);
  const { mutate } = useUpsertBookmark(productCode);

  // We want 100% sampling while we roll out journeys. This should be removed in the future for cost control purposes.
  useEffect(() => {
    if (hasRedesignExperiment) forceSessionReplay();
  }, [hasRedesignExperiment, path]);

  // stops a refresh from dropping the reader mid-chapter onto the bare
  // backdrop before the chapter's own scroll reset can run.
  useEffect(() => {
    if (!chapterNumber) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, [chapterNumber]);

  // resets on navigation since different paths have different permissions
  const [allowedPath, setAllowedPath] = useState<string | null>(null);
  // Raw path clearance — gates rendering and analytics. The exposed `isAllowed`
  // (below) also waits on the deep-link so the entrance wrapper holds for it.
  const isPathAllowed = allowedPath === path;

  // The provider's synchronous record of the member's furthest position —
  // seeded from the initial GET, advanced the moment an upsert fires so
  // gating never races the mutation round-trip. The query cache (kept fresh
  // by the mutation's onSuccess) stays the source for the global nav.
  const positionRef = useRef<BookmarkedPosition | null>(null);
  const isPositionSeededRef = useRef(false);

  // The view event fires on every call (deduped per session); only the bookmark
  // upsert is gated on the position advancing.
  const reportSectionReached = useCallback(
    (section: number) => {
      if (!chapterNumber || !isPathAllowed) return;
      trackModuleViewed(chapterNumber, section, false);
      const next = { chapter: chapterNumber, section, chapterCompleted: false };
      if (!isPositionAhead(positionRef.current, next)) return;
      positionRef.current = next;
      mutate({ module: 1, ...next });
    },
    [chapterNumber, isPathAllowed, mutate],
  );

  // Emits the ender's own view event but bookmarks the last real section, so
  // resume never lands on the ender.
  const reportChapterCompleted = useCallback(() => {
    if (!chapterNumber || !isPathAllowed) return;
    const current = positionRef.current;
    const section = Math.max(
      current?.chapter === chapterNumber ? current.section : 0,
      1,
    );
    const enderSection = MODULE_VIEWED_META[chapterNumber]?.enderSection;
    if (enderSection) trackModuleViewed(chapterNumber, enderSection, true);
    const next = { chapter: chapterNumber, section, chapterCompleted: true };
    if (!isPositionAhead(current, next)) return;
    positionRef.current = next;
    mutate({ module: 1, ...next });
  }, [chapterNumber, isPathAllowed, mutate]);

  const [isLandingSettled, setIsLandingSettled] = useState(false);
  const markLandingSettled = useCallback(() => setIsLandingSettled(true), []);
  const [isScrollCuePlaced, setIsScrollCuePlaced] = useState(false);
  const markScrollCuePlaced = useCallback(() => setIsScrollCuePlaced(true), []);

  // A `?section=` deep-link scrolls into place under the entrance wrapper; hold the
  // overlay's exit until it reports done (true when there's no deep-link).
  const [isDeepLinkResolved, setIsDeepLinkResolved] = useState(true);
  const resolveDeepLink = useCallback(() => setIsDeepLinkResolved(true), []);
  useEffect(() => {
    if (chapterNumber == null) return;
    const hasSection = new URLSearchParams(window.location.search).has(
      SECTION_QUERY_PARAM,
    );
    setIsDeepLinkResolved(!hasSection);
  }, [chapterNumber]);

  // What the entrance wrapper holds its exit on; gating/analytics use isPathAllowed.
  const isAllowed = isPathAllowed && isDeepLinkResolved;

  // Entrance handoff: the overlay finishes, the wrapper exits and completes.
  // Both recorded per chapter, so moving to the next chapter re-arms the
  // entrance without a reset effect.
  const [entranceFinishedFor, setEntranceFinishedFor] = useState<{
    chapter: number;
    exit?: EntranceExit;
  } | null>(null);
  const [entranceCompletedFor, setEntranceCompletedFor] = useState<
    number | null
  >(null);
  const finishEntrance = useCallback(
    (exit?: EntranceExit) => {
      if (chapterNumber)
        setEntranceFinishedFor({ chapter: chapterNumber, exit });
    },
    [chapterNumber],
  );
  const completeEntrance = useCallback(
    () => setEntranceCompletedFor(chapterNumber ?? null),
    [chapterNumber],
  );
  const entranceFinish =
    entranceFinishedFor?.chapter === chapterNumber ? entranceFinishedFor : null;
  const isEntranceComplete =
    !chapterNumber || entranceCompletedFor === chapterNumber;

  // Each SectionBookmark registers its sentinel by section number so a deep-link
  // can find where to scroll; a ref, so registration never triggers a render.
  const sectionAnchorsRef = useRef<Map<number, HTMLElement>>(new Map());
  const registerSectionAnchor = useCallback(
    (section: number, element: HTMLElement | null) => {
      if (element) sectionAnchorsRef.current.set(section, element);
      else sectionAnchorsRef.current.delete(section);
    },
    [],
  );
  const getSectionAnchor = useCallback(
    (section: number) => sectionAnchorsRef.current.get(section) ?? null,
    [],
  );

  // Whether the member's saved bookmark had reached this chapter when they
  // arrived. Decided once per chapter below, before this visit's own upsert
  // moves the bookmark forward. null until the bookmark has loaded.
  const [isFirstChapterView, setIsFirstChapterView] = useState<boolean | null>(
    null,
  );
  const firstViewDecidedForRef = useRef<number | null>(null);

  // Declared before the gating effect so a chapter change resets before it
  // re-decides in the same commit.
  useEffect(() => {
    setIsLandingSettled(false);
    setIsScrollCuePlaced(false);
    setIsFirstChapterView(null);
    firstViewDecidedForRef.current = null;
  }, [chapterNumber]);

  useEffect(() => {
    if (!router.isReady) return;

    if (isLoadingUser) return;
    if (!userState?.email) return;

    if (!hasRedesignExperiment) {
      router.replace(HOME_ROUTE);
      return;
    }

    if (!chapterNumber) {
      setAllowedPath(path);
      return;
    }

    // Wait for subscriptions/bookmarks requests to complete before redirect
    if (isChapterDataLoading || isBookmarkLoading) return;

    if (!isPositionSeededRef.current) {
      isPositionSeededRef.current = true;
      positionRef.current = positionFromBookmark(bookmark);
    }

    if (firstViewDecidedForRef.current !== chapterNumber) {
      firstViewDecidedForRef.current = chapterNumber;
      const position = positionRef.current;
      setIsFirstChapterView(
        position === null || position.chapter < chapterNumber,
      );
    }

    if (status === 'upcoming' && !hasChapterOverride) {
      router.replace('/account/journey');
      return;
    }

    if (status === 'active') {
      // Seeds the bookmark only; section 1's view event comes from its SectionBookmark.
      const next = {
        chapter: chapterNumber,
        section: 1,
        chapterCompleted: false,
      };
      if (isPositionAhead(positionRef.current, next)) {
        positionRef.current = next;
        mutate({ module: 1, ...next });
      }
    }

    setAllowedPath(path);
  }, [
    router.isReady,
    path,
    chapterNumber,
    hasRedesignExperiment,
    isLoadingUser,
    userState,
    status,
    isChapterDataLoading,
    isBookmarkLoading,
    bookmark,
    hasChapterOverride,
  ]);

  const [activeChapterId, setActiveChapterId] = useState<number | null>(null);
  const [isBottomsheetOpen, setIsBottomsheetOpen] = useState(false);

  // Preloader → landing → TOC: once the landing entrance settles on the
  // member's current chapter, auto-open the TOC if their bookmark hadn't
  // reached this chapter yet. Flipping the flag afterwards makes it one-shot.
  useEffect(() => {
    if (!isLandingSettled || !isFirstChapterView) return;
    if (!chapterNumber || isChapterDataLoading) return;
    if (chapterNumber !== currentChapter) return;
    setIsBottomsheetOpen(true);
    setIsFirstChapterView(false);
  }, [
    isLandingSettled,
    isFirstChapterView,
    chapterNumber,
    currentChapter,
    isChapterDataLoading,
  ]);
  const [activeScrollMotionValue, setActiveScrollMotionValue] =
    useState<MotionValue<number> | null>(null);

  // null = bookmark still loading (under the entrance wrapper); false = not a
  // first view, or the sheet has already opened. A first view of a chapter that
  // isn't the member's current one never auto-opens, so it releases too.
  const isAwaitingFirstViewToc =
    isFirstChapterView !== false &&
    !(
      isFirstChapterView &&
      !isChapterDataLoading &&
      chapterNumber !== currentChapter
    );
  // Holds the page still from chapter mount until Landing reports its entrance
  // settled and, on a first view of the current chapter, until the TOC has
  // opened (the sheet then owns the lock).
  useScrollHold(
    chapterNumber !== undefined &&
      (!isLandingSettled || isAwaitingFirstViewToc),
  );

  const authPending = isLoadingUser || !userState?.email;
  const isGateOpen = authPending || isPathAllowed;

  const contextValue = useMemo(
    () => ({
      activeChapterId,
      currentChapter,
      productCode,
      isBottomsheetOpen,
      activeScrollMotionValue,
      setActiveChapterId,
      reportSectionReached,
      reportChapterCompleted,
      setIsBottomsheetOpen,
      setActiveScrollMotionValue,
      footerDisclaimerConfig,
      markLandingSettled,
      markScrollCuePlaced,
      isScrollCuePlaced,
      isAllowed,
      isEntranceComplete,
      finishEntrance,
      entranceFinish,
      completeEntrance,
      isGateOpen,
      resolveDeepLink,
      registerSectionAnchor,
      getSectionAnchor,
    }),
    [
      activeChapterId,
      currentChapter,
      productCode,
      isBottomsheetOpen,
      activeScrollMotionValue,
      reportSectionReached,
      reportChapterCompleted,
      footerDisclaimerConfig,
      markLandingSettled,
      markScrollCuePlaced,
      isScrollCuePlaced,
      isAllowed,
      isEntranceComplete,
      finishEntrance,
      entranceFinish,
      completeEntrance,
      isGateOpen,
      resolveDeepLink,
      registerSectionAnchor,
      getSectionAnchor,
    ],
  );

  return (
    <JourneyContext.Provider value={contextValue}>
      {children}
    </JourneyContext.Provider>
  );
};
